import React, { useMemo } from "react";
import t from "../../../../../../i18n";
import { colors } from "@ui/Theme";
import { stableColor } from "@ui";
import { useQOI } from "../../../useQOI";
import {
  REST_PROJECT_AGGREGATION_TARGET_ID,
  useProjectAggegation,
  useProjectAggregationTargets,
} from "../useProjectAggregation";
import { aggregateSerieValues, aggregationByProjectAndPI } from "../../../utils/series";
import { REPORTING_POINT_METADATA } from "../../../../../Project/Chart/PointMetadata";
import { useTranslations } from "../../../../../../api/useTranslations";
import { getPointProjectCount } from "../../../utils/projectCount";
import type { PerformanceReporting } from "../../../../../../api/useReporting";

export const FLEET_INTERNAL_LOSSES_ID = "FleetInternalLosses";

const aggregatedSeries: Highcharts.SeriesColumnOptions = {
  type: "column",
  yAxis: "performance-internal-losses",
  stack: 1,
  stacking: "normal",
  replicateInNavigator: false,
  borderWidth: 0,
  dataGrouping: {
    forced: true,
    units: [["month", [1]]],
  },
};

function createAggregatedSerie(
  target: { id: string; name: string },
  index: number,
  data: { x: number; y: number | null }[],
) {
  return {
    ...aggregatedSeries,
    id: `InternalGapAggregation-${target.name}-${index}`,
    name: target.name,
    color: target.id === REST_PROJECT_AGGREGATION_TARGET_ID ? "#888888" : stableColor(target.id),
    data: data.filter(({ y }) => y !== null),
    index,
    legendIndex: index,
    showInLegend: data.some(d => d.y !== 0),
  };
}

export function useSeries(reporting: PerformanceReporting) {
  const translation = useTranslations();
  const [selectedProjectAggregation] = useProjectAggegation({});
  const [qoi] = useQOI(reporting);
  const aggregationTargets = useProjectAggregationTargets(reporting);

  const totalInternalLossesSerie: Highcharts.SeriesColumnOptions = useMemo(
    () => ({
      id: FLEET_INTERNAL_LOSSES_ID,
      name: t("reporting.internal_gaps_evolution.chart.internal_gaps", translation),
      type: "column",
      color: colors.raw.lightNitrogen100,
      yAxis: "performance-internal-losses",
      data: [],
      replicateInNavigator: false,
      index: 0,
      showInLegend: true,
      legendIndex: 0,
      stack: 2,
      dataGrouping: {
        forced: true,
        units: [["month", [1]]],
      },
    }),
    [translation],
  );

  return React.useMemo(() => {
    const qois = Object.values(reporting.qoiById).filter(({ name }) => name === qoi?.name);
    const qoiIds = new Set(qois.map(({ id }) => id));
    const qoiProjectIds = new Set(qois.map(({ projectId }) => projectId));

    totalInternalLossesSerie.data = reporting.periods.map(
      ({ period, driftedProjectIds, values }) => {
        const pointCount = getPointProjectCount(qoiIds, qoiProjectIds, values, driftedProjectIds);

        return {
          x: new Date(period).getTime(),
          y: aggregateSerieValues(
            values,
            (currentValue, measured, expected) => (currentValue ?? 0) + measured - expected,
            value => reporting.qoiById[value.qoiId]?.name === qoi?.name,
          ),
          custom: {
            type: REPORTING_POINT_METADATA,
            ...pointCount,
          },
        };
      },
    );

    const aggregatedSeries = aggregationTargets
      .map((target, i) =>
        createAggregatedSerie(
          target,
          i,
          reporting.periods.map(({ period, driftedProjectIds, values }) => ({
            x: new Date(period).getTime(),
            y: aggregateSerieValues(
              values,
              (currentValue, measured, expected) => (currentValue ?? 0) + measured - expected,
              aggregationByProjectAndPI(
                reporting,
                qoi?.name ?? null,
                selectedProjectAggregation,
                target.id,
              ),
            ),
            custom: {
              type: REPORTING_POINT_METADATA,
              ...getPointProjectCount(qoiIds, qoiProjectIds, values, driftedProjectIds),
            },
          })) || [],
        ),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((serie, i) => ({ ...serie, index: i + 1, legendIndex: i + 1 }));

    return [totalInternalLossesSerie, ...aggregatedSeries];
  }, [
    aggregationTargets,
    reporting,
    qoi?.name,
    selectedProjectAggregation,
    totalInternalLossesSerie,
  ]);
}
